"use client";

import React from "react";
import {
  CARD_SIZES_1UP,
  CARD_SIZES_2UP_40,
  CARD_SIZES_2UP_60,
  CARD_SIZES_2UP_SECTION,
  CARD_SIZES_CAROUSEL,
  CARD_SIZES_HERO,
} from "@/lib/imageSizes";
import { CardSection as CardSectionType, SectionWidth } from "@/types/cms";
import { MinimalDialogProvider } from "@/components/dialog/minimalDialog/MinimalDialog";
import SectionHeader from "@/components/sections/utils/SectionHeader";
import CardsGrid from "@/components/cards/cardsGrid/cardsGrid";
import styles from "./CardSection.module.css";
import { getSectionSpacingClasses } from "@/components/sections/utils/spacing";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import { Separator } from "@/components/separator/separator";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/carousel/carousel";
import SectionButtonsDialog from "@/components/sections/utils/SectionButtonsDialog";
import { useCarouselAutoplay } from "@/components/carousel/useCarouselAutoplay";
import HeroTitleWrapper from "@/components/HeroTitleWrapper";
import HeroButtonWrapper from "@/components/HeroButtonWrapper";
import HeroImageWrapper from "@/components/HeroImageWrapper";
import { setHeroImageLoaded, setModeLoaded } from "@/stores/pageLoadingStore";

interface CardSectionProps {
  data: CardSectionType & { type?: CardSectionType["type"] | "type_3upCenter" };
}

// Testimonial Carousel Component with autoplay
const TestimonialCarousel = ({
  data,
  content_cards,
  getCardVariant,
  sectionId,
  sectionClass,
  containerClass,
  callout,
  showDivider,
  Header,
}: {
  data: any;
  content_cards: any[];
  getCardVariant: (card: any) => "default" | "testimonial" | "price";
  sectionId: string;
  sectionClass: string;
  containerClass: string;
  callout: boolean;
  showDivider: boolean;
  Header: () => React.JSX.Element;
}) => {
  const [api, setApi] = React.useState<any>();
  
  // Enable autoplay with 3 second delay
  useCarouselAutoplay(api, 3000);

  return (
    <section
      id={sectionId}
      className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
    >
      <div className={containerClass}>
        {showDivider && (
          <div className={styles.divider}><Separator /></div>
        )}
        <Header />
        <div className={styles.testimonialCarouselWrap}>
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              dragFree: false,
              containScroll: "trimSnaps",
              slidesToScroll: 1,
              loop: true,
            }}
          >
            <CarouselContent className={styles.testimonialCarouselContent}>
              {content_cards.map((card: any, idx: number) => (
                <CarouselItem
                  key={card.id ?? `${idx}-${card.header || 'card'}`}
                  className={styles.testimonialCarouselItem}
                >
                  <CardContentItem card={card} variant={getCardVariant(card)} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>
    </section>
  );
};

const CardSectionInner = ({ data }: CardSectionProps) => {
  // Read the CMS custom ID field (cardSection_customID in the schema).
  // Derived above the hooks so the loading-gate effect can depend on the hero
  // flag. It previously had no dependency array, so it re-fired the store
  // writes on every render and woke every valtio subscriber.
  const rawCustomId = (data as any)?.cardSection_customID as
    | string
    | undefined;
  const customId =
    typeof rawCustomId === "string" && rawCustomId.startsWith("#")
      ? rawCustomId.slice(1)
      : rawCustomId;
  const isHeroSection =
    typeof customId === "string" &&
    customId.trim().toLowerCase().replace(/^#/, "") === "hero";

  // Satisfy the page-load gates once the hero section mounts (no Mode system
  // in this template).
  React.useEffect(() => {
    if (!isHeroSection) return;
    setModeLoaded(true);
    setHeroImageLoaded(true);
  }, [isHeroSection]);

  if (!data) return null;

  const sectionId = customId || "features";
  const spacing = getSectionSpacingClasses(data as any);
  const shadowClass = (data as any)?.shadow === false ? styles.noShadow : undefined;
  const shadow = (data as any)?.shadow !== false; // Default to true unless explicitly false
  const roundedClass = (data as any)?.rounded === false ? styles.noRadius : undefined;
  const callout = (data as any)?.callout === true;
  const widthToken = (data as any)?.sectionWidth as SectionWidth | undefined;
  const widthClass = widthToken ? `w-${widthToken}` : undefined;
  const isTestimonialSection =
    typeof customId === "string" &&
    customId.trim().toLowerCase() === "testimonial";
  const isPriceSection =
    typeof customId === "string" &&
    (customId.trim().toLowerCase() === "price" ||
      customId.trim().toLowerCase() === "pricing");

  // Helper to sort cards by order field if present, otherwise by id descending (newest first)
  const sortCards = (cards: any[]): any[] => {
    if (!Array.isArray(cards) || cards.length === 0) return cards;
    
    // Check if cards have an explicit order field
    const hasOrderField = cards.some(card => typeof card?.order === 'number');
    
    if (hasOrderField) {
      // Sort by order field if present
      return [...cards].sort((a, b) => {
        const orderA = typeof a?.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
        const orderB = typeof b?.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
    }
    
    // Fallback: sort by id descending (newest first)
    // This helps when the CMS doesn't maintain drag-and-drop order
    return [...cards].sort((a, b) => {
      const idA = typeof a?.id === 'number' ? a.id : 0;
      const idB = typeof b?.id === 'number' ? b.id : 0;
      return idB - idA; // Descending order (newest first)
    });
  };

  // Helper to determine card variant based on section or card customID
  const getCardVariant = (card: any): "default" | "testimonial" | "price" => {
    const cardCustomId =
      typeof card?.contentCard_customID === "string"
        ? card.contentCard_customID.toLowerCase().trim()
        : undefined;
    if (
      (cardCustomId && cardCustomId.includes("price")) ||
      isPriceSection
    )
      return "price";
    if (cardCustomId === "testimonial" || isTestimonialSection) return "testimonial";
    return "default";
  };

  const sectionClass = [
    'section',
    spacing,
    shadowClass,
    roundedClass,
    isHeroSection ? styles.heroSpaceTop : undefined,
  ]
    .filter(Boolean)
    .join(" ");
  const containerClass = [
    'section-container',
    widthClass,
    callout ? styles.calloutInner : undefined,
  ]
    .filter(Boolean)
    .join(' ');
  const columnGapToken = (data as any)?.columnGap as 'sm' | 'md' | 'lg' | undefined;
  const gapClass = columnGapToken === 'sm' ? styles.gapSm : columnGapToken === 'lg' ? styles.gapLg : styles.gapMd;
  const overflowBehavior = (data as any)?.overflowBehavior as 'wrap' | 'carousel' | undefined;
  const enableCarousel = overflowBehavior === 'carousel' || isTestimonialSection || isPriceSection;

  const showDivider = (data as any)?.divider === true;
  const buttons = (data as any)?.buttons as any[] | undefined;

  // Sort content_cards once at the top level
  const rawCards = (data as any)?.content_cards || [];
  const content_cards = sortCards(rawCards);

  const Header = () => (
    <div
      className={
        isHeroSection
          ? styles.heroHeader
          : isTestimonialSection
          ? styles.testimonialHeader
          : isPriceSection
          ? styles.priceHeader
          : undefined
      }
    >
      <SectionHeader
        content={data.content}
        align={data.contentAlign}
        textAlign={data.textJustify}
        headerWidth={(data as any)?.headerWidth}
      />
    </div>
  );

  // 1up100vh layout: single image filling grid with 90vh max height
  if (data.type === 'type_1up100vh') {
    const firstCard = content_cards[0];

    return (
      <section
        id={customId || 'one-up'}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={containerClass}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          <div className={styles.oneUpWrap}>
            {firstCard && <CardContentItem card={firstCard as any} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={CARD_SIZES_1UP} />}
          </div>
        </div>
      </section>
    );
  }

  // 1up standard: render the single card at full section width
  if (data.type === 'type_1up') {
    const firstCard = content_cards[0];
    // Carousel overflow: render cards horizontally using Carousel primitives
    if (enableCarousel && content_cards.length > 1) {
      return (
        <section
          id={customId || 'one-up'}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={styles.divider}><Separator /></div>
            )}
            <Header />
            <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
              <Carousel>
                <CarouselContent className={styles.carouselContentSm}>
                  {content_cards.map((card: any, idx: number) => (
                    <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                      <CardContentItem card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className={styles.hideSm}>
              {firstCard && (
                <div>
                  <CardContentItem card={firstCard as any} shadow={shadow} variant={isTestimonialSection ? "testimonial" : "default"} imageSizes={CARD_SIZES_1UP} />
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    // Default: wrap behavior (single card full width)
    return (
      <section
        id={customId || 'one-up'}
        className={[
          callout ? `${sectionClass} ${styles.callout}` : sectionClass,
          isHeroSection ? styles.heroSection : undefined,
        ].filter(Boolean).join(' ')}
      >
        {/* Decorative grid background for hero sections */}
        {isHeroSection && (
          <div className={`${styles.heroGridBg} grid-bg`} />
        )}
        <div
          className={[
            containerClass,
            gapClass,
            isHeroSection ? styles.heroInner : undefined,
          ].filter(Boolean).join(' ')}
        >
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          {isHeroSection ? (
            <HeroTitleWrapper>
              <Header />
            </HeroTitleWrapper>
          ) : (
            <Header />
          )}
          {Array.isArray(buttons) && buttons.length > 0 && (
            isHeroSection ? (
              <HeroButtonWrapper className={styles.heroButtons}>
                <SectionButtonsDialog
                  buttons={buttons}
                  align={data.contentAlign}
                  prioritizePrimary={isHeroSection}
                />
              </HeroButtonWrapper>
            ) : (
              <SectionButtonsDialog
                buttons={buttons}
                align={data.contentAlign}
                prioritizePrimary={isHeroSection}
              />
            )
          )}
          {firstCard && (
            isHeroSection ? (
              <HeroImageWrapper>
                {/* Padding handled by `.heroCard` via --container-padding-x
                   (24px mobile / 32px tablet / 48px desktop). No inline
                   override — hardcoding 48px made the mobile image tiny. */}
                <div className={`${styles.heroCardSpacing} ${styles.heroCard}`}>
                  <CardContentItem card={firstCard as any} shadow={shadow} imageSizes={CARD_SIZES_HERO} priority quality={85} />
                </div>
              </HeroImageWrapper>
            ) : (
              <div>
                <CardContentItem card={firstCard as any} shadow={shadow} imageSizes={CARD_SIZES_1UP} />
              </div>
            )
          )}
        </div>
      </section>
    );
  }

  // 2up 50/50
  if (data.type === 'type_2up50/50') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={styles.divider}><Separator /></div>
            )}
            <Header />
            <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
              <Carousel>
                <CarouselContent className={styles.carouselContentSm}>
                  {cardsArr.map((card: any, idx: number) => (
                    <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                      <CardContentItem card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className={styles.hideSm}>
              {/* fall back to existing single/dual layout on larger screens */}
              {cardsArr.length === 1 ? (
                <div className={styles.twoUp5050Grid}>
                  <div className={styles.twoUp5050Text}>
                    <Header />
                  </div>
                  <div className={styles.twoUp5050Image}>
                    <CardContentItem card={cardsArr[0] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_SECTION} />
                  </div>
                </div>
              ) : (
                <CardsGrid type={data.type} cards={[...cardsArr]} shadow={shadow} />
              )}
            </div>
          </div>
        </section>
      );
    }
    // If only one card, render like the 60/40/40/60 variants: header + image side-by-side
    if (cardsArr.length === 1) {
      const firstCard = cardsArr[0] as any;
      return (
        <section id={customId || "two-up"} className={sectionClass}>
        <div className={['section-container', widthClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
            <div className={styles.twoUp5050Grid}>
              <div className={styles.twoUp5050Text}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
              </div>
              <div className={styles.twoUp5050Image}>
                <CardContentItem card={firstCard} shadow={shadow} variant={getCardVariant(firstCard)} imageSizes={CARD_SIZES_2UP_SECTION} />
              </div>
            </div>
          </div>
        </section>
      );
    }
    // Otherwise render standard header + cards grid
    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          <Header />
          {Array.isArray(buttons) && buttons.length > 0 && (
            <SectionButtonsDialog
              buttons={buttons}
              align={data.contentAlign}
              prioritizePrimary={isHeroSection}
            />
          )}
          <CardsGrid type={data.type} cards={[...cardsArr]} shadow={shadow} />
        </div>
      </section>
    );
  }

  // 3up Center: render cards in a single centered column matching header width
  if ((data as any).type === 'type_3upCenter') {
    if (enableCarousel && content_cards.length > 0) {
      return (
        <section
          id={customId || "three-up-center"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={styles.divider}><Separator /></div>
            )}
            <Header />
            <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
              <Carousel>
                <CarouselContent className={styles.carouselContentSm}>
                  {content_cards.map((card: any, idx: number) => (
                    <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                      <CardContentItem card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className={styles.hideSm}>
              <div className={styles.centerColumn}>
                {content_cards.map((card: any, idx: number) => (
                <CardContentItem key={card.id ?? `${idx}-${card.header || 'card'}`} card={card} shadow={shadow} variant={getCardVariant(card)} />
                ))}
              </div>
            </div>
          </div>
        </section>
      );
    }
    return (
      <section
        id={customId || "three-up-center"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={containerClass}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          <Header />
          {Array.isArray(buttons) && buttons.length > 0 && (
            <SectionButtonsDialog
              buttons={buttons}
              align={data.contentAlign}
              prioritizePrimary={isHeroSection}
            />
          )}
          <div className={styles.centerColumn}>
            {content_cards.map((card: any, idx: number) => (
                <CardContentItem key={card.id ?? `${idx}-${card.header || 'card'}`} card={card} shadow={shadow} variant={getCardVariant(card)} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  // type_2up60/40: two cards side-by-side with header above; single card pairs with header
  if (data.type === 'type_2up60/40') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={styles.divider}><Separator /></div>
            )}
            <Header />
            <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
              <Carousel>
                <CarouselContent className={styles.carouselContentSm}>
                  {cardsArr.map((card: any, idx: number) => (
                    <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                      <CardContentItem card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className={styles.hideSm}>
              {cardsArr.length >= 2 ? (
                <>
                  <div className={styles.twoUp6040Grid}>
                    <div className={styles.twoUp6040Text}>
                      <CardContentItem card={cardsArr[0] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_60} />
                    </div>
                    <div className={styles.twoUp6040Image}>
                      <CardContentItem card={cardsArr[1] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_40} />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.twoUp6040Grid}>
                  <div className={styles.twoUp6040Text}>
                    <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
                  </div>
                  <div className={styles.twoUp6040Image}>
                    {cardsArr[0] && <CardContentItem card={cardsArr[0] as any} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={CARD_SIZES_2UP_40} />}
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          {cardsArr.length >= 2 ? (
            <>
              <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
              <div className={styles.twoUp6040Grid}>
                <div className={styles.twoUp6040Text}>
                  <CardContentItem card={cardsArr[0] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_60} />
                </div>
                <div className={styles.twoUp6040Image}>
                  <CardContentItem card={cardsArr[1] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_40} />
                </div>
              </div>
            </>
          ) : (
            <div className={styles.twoUp6040Grid}>
              <div className={styles.twoUp6040Text}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
              </div>
              <div className={styles.twoUp6040Image}>
                {cardsArr[0] && <CardContentItem card={cardsArr[0] as any} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={CARD_SIZES_2UP_40} />}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // 2up 40/60: two cards with header above; single card pairs with header
  if (data.type === 'type_2up40/60') {
    const cardsArr = content_cards;
    if (enableCarousel && cardsArr.length > 0) {
      return (
        <section
          id={customId || "two-up"}
          className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
        >
          <div className={containerClass}>
            {showDivider && (
              <div className={styles.divider}><Separator /></div>
            )}
            <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
            <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
              <Carousel>
                <CarouselContent className={styles.carouselContentSm}>
                  {cardsArr.map((card: any, idx: number) => (
                    <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                      <CardContentItem card={card} shadow={shadow} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            <div className={styles.hideSm}>
              {cardsArr.length >= 2 ? (
                <>
                  <div className={styles.twoUp4060Grid}>
                    <div className={styles.twoUp4060Image}>
                      <CardContentItem card={cardsArr[0] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_40} />
                    </div>
                    <div className={styles.twoUp4060Text}>
                      <CardContentItem card={cardsArr[1] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_60} />
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.twoUp4060Grid}>
                  <div className={styles.twoUp4060Image}>
                    {cardsArr[0] && <CardContentItem card={cardsArr[0] as any} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={CARD_SIZES_2UP_40} />}
                  </div>
                  <div className={styles.twoUp4060Text}>
                    <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      );
    }

    return (
      <section
        id={customId || "two-up"}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          {cardsArr.length >= 2 ? (
            <>
              <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
              <div className={styles.twoUp4060Grid}>
                <div className={styles.twoUp4060Image}>
                  <CardContentItem card={cardsArr[0] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_40} />
                </div>
                <div className={styles.twoUp4060Text}>
                  <CardContentItem card={cardsArr[1] as any} shadow={shadow} imageSizes={CARD_SIZES_2UP_60} />
                </div>
              </div>
            </>
          ) : (
            <div className={styles.twoUp4060Grid}>
              <div className={styles.twoUp4060Image}>
                {cardsArr[0] && <CardContentItem card={cardsArr[0] as any} shadow={shadow} variant={getCardVariant(cardsArr[0])} imageSizes={CARD_SIZES_2UP_40} />}
              </div>
              <div className={styles.twoUp4060Text}>
                <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  // Default grid-based layouts (e.g., 3up/4up)
  if (enableCarousel && content_cards.length > 0) {
    // For testimonials, show carousel on all screen sizes with peek effect (full-bleed).
    // Pricing sections should respect sectionWidth on desktop, so they use the normal
    // "mobile carousel + desktop grid" rendering below.
    if (isTestimonialSection) {
      return (
        <TestimonialCarousel
          data={data}
          content_cards={content_cards}
          getCardVariant={getCardVariant}
          sectionId={sectionId}
          sectionClass={sectionClass}
          containerClass={containerClass}
          callout={callout}
          showDivider={showDivider}
          Header={Header}
        />
      );
    }

    // Regular carousel: mobile only, desktop shows grid
    return (
      <section
        id={sectionId}
        className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
      >
        <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
          {showDivider && (
            <div className={styles.divider}><Separator /></div>
          )}
          <Header />
          <div className={[styles.carouselWrap, styles.onlySm, styles.carouselFullSm].join(' ')}>
            <Carousel>
              <CarouselContent className={styles.carouselContentSm}>
                {content_cards.map((card: any, idx: number) => (
                  <CarouselItem key={card.id ?? `${idx}-${card.header || 'card'}`} className={styles.carouselItemSm}>
                    <CardContentItem card={card} variant={getCardVariant(card)} imageSizes={CARD_SIZES_CAROUSEL} />
                  </CarouselItem>
                ))}
              </CarouselContent>

            </Carousel>
          </div>
          <div className={styles.hideSm}>
            <CardsGrid
              type={data.type}
              cards={content_cards}
              getCardVariant={getCardVariant as any}
            />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id={sectionId}
      className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
    >
      <div className={[containerClass, gapClass].filter(Boolean).join(' ')}>
        {showDivider && (
          <div className={styles.divider}><Separator /></div>
        )}
        <SectionHeader content={data.content} align={data.contentAlign} textAlign={data.textJustify} headerWidth={(data as any)?.headerWidth} />
        {Array.isArray(buttons) && buttons.length > 0 && (
          <SectionButtonsDialog
            buttons={buttons}
            align={data.contentAlign}
            prioritizePrimary={isHeroSection}
          />
        )}
        <CardsGrid
          type={data.type}
          cards={content_cards}
        />
      </div>
    </section>
  );
};

/**
 * Wrapped so every dialog card in this section shares ONE dialog — the
 * component has ~14 separate `return (<section>…)` branches, and this is the
 * single point that covers all of them.
 */
const CardSection = (props: CardSectionProps) => (
  <MinimalDialogProvider>
    <CardSectionInner {...props} />
  </MinimalDialogProvider>
);

export default CardSection; 